import { ReactFlowProvider } from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { useCallback } from 'react';

import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import { Box, Typography } from '@mui/material';

import { useToastService } from '@@common';

import { EnumModel, SchemaHierarchyModel } from '../../api/api';
import DomainEntityFlow from './DomainEntityFlow';
import { DomainGraphNodeFilter, DomainMetadataUpdateItem } from './DomainGraphUtils';
import { useUpdateDomainMetadata } from './Hooks/useUpdateDomainMetadata';

export const DEFAULT_NODE_FILTERS: DomainGraphNodeFilter[] = ['root', 'entity', 'enum'];

type DomainEntityGraphViewProps = {
	contextId: string;
	entities: SchemaHierarchyModel[];
	enums: EnumModel[];
	selectedEntityId?: string;
	selectedEnumId?: string;
	enabledNodeFilters: DomainGraphNodeFilter[];
	searchText: string;
	autoLayoutRequest: number;
	onEntitySelected?: (entityId: string) => void;
	onEnumSelected?: (enumId: string) => void;
};

export default function DomainEntityGraphView({
	contextId,
	entities,
	enums,
	selectedEntityId,
	selectedEnumId,
	enabledNodeFilters,
	searchText,
	autoLayoutRequest,
	onEntitySelected,
	onEnumSelected,
}: DomainEntityGraphViewProps) {
	const toastService = useToastService();
	const { batchUpdateDomainMetadata } = useUpdateDomainMetadata(contextId);

	const handleBatchDomainMetadataChanged = useCallback(
		async (items: DomainMetadataUpdateItem[]) => {
			try {
				await batchUpdateDomainMetadata(items);
			} catch (error) {
				console.error('Failed to save domain graph positions', error);
				toastService.error('Failed to save graph positions');
			}
		},
		[batchUpdateDomainMetadata, toastService],
	);

	if (entities.length === 0 && enums.length === 0) {
		return (
			<Box sx={{ flexGrow: 1, minHeight: 0, display: 'grid', placeItems: 'center', p: 3 }}>
				<Box
					sx={{
						width: '100%',
						maxWidth: 380,
						p: 3.5,
						textAlign: 'center',
						border: '1px dashed',
						borderColor: 'divider',
						borderRadius: 2,
						bgcolor: 'action.hover',
					}}
				>
					<Box
						sx={{
							display: 'inline-grid',
							placeItems: 'center',
							width: 48,
							height: 48,
							mb: 1.5,
							borderRadius: '50%',
							color: 'primary.main',
							bgcolor: 'background.paper',
							boxShadow: 1,
						}}
					>
						<AccountTreeOutlinedIcon />
					</Box>
					<Typography variant="h6" sx={{ fontWeight: 600 }}>
						Your domain graph is empty
					</Typography>
					<Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
						Create an entity or enum from the Artifacts tree to map your domain and its relationships here.
					</Typography>
				</Box>
			</Box>
		);
	}

	return (
		<Box sx={{ flexGrow: 1, minHeight: 0, height: '100%' }}>
			<ReactFlowProvider>
				<DomainEntityFlow
					entities={entities}
					enums={enums}
					selectedEntityId={selectedEntityId}
					selectedEnumId={selectedEnumId}
					enabledNodeFilters={enabledNodeFilters}
					searchText={searchText}
					autoLayoutRequest={autoLayoutRequest}
					onEntitySelected={onEntitySelected}
					onEnumSelected={onEnumSelected}
					onBatchDomainMetadataChanged={handleBatchDomainMetadataChanged}
				/>
			</ReactFlowProvider>
		</Box>
	);
}
